import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight } from "lucide-react";
import Link from "next/link";
import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Card, CardContent, CardHeader } from "@/lib/components/card";
import { formatDate, isoDate, type TimelineModel } from "@/lib/timeline-data";
import { getModelFamilyIcon } from "@llmgateway/shared/components";
export const GUTTER = "w-10 md:w-14";
export function FamilyMark(t0) {
  const $ = _c(4);
  const {
    family
  } = t0;
  let t1;
  if ($[0] !== family) {
    t1 = getModelFamilyIcon(family);
    $[0] = family;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const Icon = t1;
  let t2;
  if ($[2] !== Icon) {
    t2 = <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/70 bg-muted/50"><Icon className="h-5 w-5" /></span>;
    $[2] = Icon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export function ModelFact(t0) {
  const $ = _c(15);
  const {
    model
  } = t0;
  let t1;
  if ($[0] !== model.name) {
    t1 = <span className="font-medium text-foreground">{model.name}</span>;
    $[0] = model.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = model.releasedAt ? " released by " : " added by ";
  let t3;
  if ($[2] !== model.providerName) {
    t3 = <span className="font-medium text-foreground">{model.providerName}</span>;
    $[2] = model.providerName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== model.releasedAt) {
    t4 = model.releasedAt ? <>{" "}on{" "}<time dateTime={isoDate(model.releasedAt)} className="font-medium text-foreground">{formatDate(model.releasedAt)}</time></> : null;
    $[4] = model.releasedAt;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== model.addedAt || $[7] !== model.releasedAt) {
    t5 = model.addedAt ? <>{model.releasedAt ? " and added" : ""} to LLM Gateway on{" "}<time dateTime={isoDate(model.addedAt)} className="font-medium text-foreground">{formatDate(model.addedAt)}</time></> : null;
    $[6] = model.addedAt;
    $[7] = model.releasedAt;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <p className="text-sm leading-relaxed text-muted-foreground">{t1} was{t2}{t3}{t4}{t5}.</p>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
interface ModelCardProps {
  model: TimelineModel;
  latestReleasedAt: string | null;
}
export function ModelCard(t0) {
  const $ = _c(35);
  const {
    model,
    latestReleasedAt
  } = t0;
  let t1;
  if ($[0] !== model.family) {
    t1 = <FamilyMark family={model.family} />;
    $[0] = model.family;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== model.name) {
    t2 = <h3 className="font-display text-base font-semibold leading-tight md:text-lg">{model.name}</h3>;
    $[2] = model.name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== model.significant) {
    t3 = model.significant ? <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 px-1.5 py-0 text-[10px] font-medium text-amber-600 dark:text-amber-400">Flagship</Badge> : null;
    $[4] = model.significant;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== latestReleasedAt || $[7] !== model.releasedAt) {
    t4 = model.releasedAt && model.releasedAt === latestReleasedAt ? <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">Latest</Badge> : null;
    $[6] = latestReleasedAt;
    $[7] = model.releasedAt;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className="flex flex-wrap items-center gap-2">{t2}{t3}{t4}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== model.id) {
    t6 = <span className="font-mono text-[11px]">{model.id}</span>;
    $[13] = model.id;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== model.providerName || $[16] !== t6) {
    t7 = <p className="mt-0.5 text-xs text-muted-foreground">{model.providerName} ·{" "}{t6}</p>;
    $[15] = model.providerName;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t5 || $[19] !== t7) {
    t8 = <div className="min-w-0 flex-1">{t5}{t7}</div>;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t1 || $[22] !== t8) {
    t9 = <CardHeader className="px-4 pb-2"><div className="flex items-start gap-3">{t1}{t8}</div></CardHeader>;
    $[21] = t1;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== model) {
    t10 = <ModelFact model={model} />;
    $[24] = model;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = `/models/${encodeURIComponent(model.id)}`;
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ArrowUpRight className="h-3 w-3" />;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11) {
    t13 = <Button asChild={true} variant="ghost" size="sm" className="mt-2 h-auto px-0 text-primary hover:bg-transparent hover:text-primary hover:underline"><Link href={t11} className="inline-flex items-center gap-1 text-xs font-medium">{"Pricing, providers & uptime"}{t12}</Link></Button>;
    $[27] = t11;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t13) {
    t14 = <CardContent className="px-4">{t10}{t13}</CardContent>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14 || $[33] !== t9) {
    t15 = <Card className="group h-full gap-0 border-border/70 bg-card/60 py-4 backdrop-blur transition-all hover:border-primary/40 hover:bg-card hover:shadow-md">{t9}{t14}</Card>;
    $[32] = t14;
    $[33] = t9;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
}